<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import axios from 'axios';

import Button from 'dashboard/components-next/button/Button.vue';
import AgentSelector from '../components/intelligent-adjustments/AgentSelector.vue';
import AdjustmentBlocks from '../components/intelligent-adjustments/AdjustmentBlocks.vue';
import CustomPromptInput from '../components/intelligent-adjustments/CustomPromptInput.vue';
import AlterationDisplay from '../components/intelligent-adjustments/AlterationDisplay.vue';

const router = useRouter();
const store = useStore();

// State
const isLoading = ref(false);
const isSubmitting = ref(false);
const selectedAgentId = ref(null);
const selectedAgent = ref(null);

// Sistema labels e teams
const systemLabels = ref([]);
const systemTeams = ref([]);
const showAdjustmentArea = ref(false);
const showModeSelection = ref(false);
const selectedMode = ref(null); // 'guided' ou 'diagnostic'
const showDiagnostic = ref(false);
const diagnosticResult = ref('');
const diagnosticOriginalResult = ref('');
const diagnosticMappedResult = ref('');
const isDiagnosticLoading = ref(false);

// Mensagens de loading para diagnóstico
const diagnosticLoadingMessages = [
  'Analisando a estrutura do prompt do agente',
  'Identificando padrões de comportamento',
  'Verificando consistência das instruções',
  'Avaliando eficiência das respostas',
  'Detectando possíveis redundâncias',
  'Analisando fluxo conversacional',
  'Mapeando oportunidades de otimização',
  'Gerando relatório de diagnóstico'
];
const diagnosticMessageIndex = ref(0);
let diagnosticMessageInterval = null;
const adjustmentOptions = ref([]);
const selectedOption = ref(null);
const customPrompt = ref('');
const adjustmentResult = ref(null);
const selectedAlterations = ref([]);
const processedAlterations = ref([]);
const alterationProcessingStats = ref(null);
const showAlterations = ref(false);
const step2Active = ref(false);
const step3Active = ref(false);
const showInitialPage = ref(false); // Mudado para false para ir direto à seleção de agente

// Debug state for alterations JSON
const showAlterationsDebug = ref(false);
const alterationsOriginalJson = ref(null);

// Controle de reports enviados
const reportsSent = ref({
  treatment: false,  // Se já foi enviado report automático de tratamento
  client: false      // Se o cliente já reportou manualmente
});

// Debug console state
const showDebugConsole = ref(false);
const isDebugMode = ref(false);
const debugFormattedOutput = ref('');
const debugSelectedAgentId = ref('');
const debugCustomPrompt = ref('');
const debugAgentData = ref(null);
const debugManualResponse = ref('');
const showDebugResponseInput = ref(false);
const debugModeReason = ref('');

// Loading messages
const loadingMessages = [
  'Analisando o agente e preparando sugestões personalizadas',
  'Processando informações do agente inteligente',
  'Identificando oportunidades de otimização'
];
const loadingMessageIndex = ref(0);
let loadingMessageInterval = null;

// Get authentication headers from cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) {
    console.error('cw_d_session_info cookie não encontrado');
    return {};
  }
  try {
    const sessionInfo = JSON.parse(decodeURIComponent(cookie));

    // Obter o CSRF token do meta tag ou do cookie
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
                      getCookie('XSRF-TOKEN') ||
                      getCookie('_chatwoot_session');

    const headers = {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };

    // Adicionar CSRF token se disponível
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    return headers;
  } catch (e) {
    console.error('Erro ao analisar cw_d_session_info:', e);
    return {};
  }
};

// Computed properties
const isFormValid = computed(() => {
  return selectedAgentId.value && (selectedOption.value || customPrompt.value.trim());
});

// Escapa HTML para evitar injeção - usado seletivamente
const escapeHtml = (text = '') =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

// Função auxiliar para detectar caracteres Unicode corrompidos
const hasCorruptedUnicode = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  // Converter para string se necessário
  const str = typeof value === 'string' ? value : JSON.stringify(value);

  // Detectar padrões de Unicode corrompido
  // \u0000 é um null character que indica corrupção
  // Também verificar sequências inválidas como \u0000e7\u0000e3
  const corruptedPatterns = [
    /\\u0000/,  // Null character em escape Unicode
    /\x00/,     // Null character literal
    /[\u0000]/  // Null character direto
  ];

  return corruptedPatterns.some(pattern => pattern.test(str));
};

// Contador de alterações rejeitadas por Unicode corrompido
let corruptedAlterationsCount = 0;

// Função auxiliar para validar se uma alteração tem valores válidos
const isValidAlteration = (alteration) => {
  // Verificar campos obrigatórios
  if (!alteration.variable || !alteration.action || alteration.newValue === undefined) {
    console.warn('Alteração inválida - campos obrigatórios faltando:', alteration);
    return false;
  }

  // Verificar se tem Unicode corrompido em qualquer campo
  if (hasCorruptedUnicode(alteration.newValue)) {
    console.warn('Alteração rejeitada - Unicode corrompido em newValue:', alteration);
    corruptedAlterationsCount++;
    return false;
  }

  if (alteration.currentValue !== undefined && hasCorruptedUnicode(alteration.currentValue)) {
    console.warn('Alteração rejeitada - Unicode corrompido em currentValue:', alteration);
    corruptedAlterationsCount++;
    return false;
  }

  if (alteration.itemName && hasCorruptedUnicode(alteration.itemName)) {
    console.warn('Alteração rejeitada - Unicode corrompido em itemName:', alteration);
    corruptedAlterationsCount++;
    return false;
  }

  // Se newValue é uma string JSON, tentar fazer parse e verificar novamente
  if (typeof alteration.newValue === 'string' &&
      (alteration.newValue.startsWith('{') || alteration.newValue.startsWith('['))) {
    try {
      const parsed = JSON.parse(alteration.newValue);
      if (hasCorruptedUnicode(JSON.stringify(parsed))) {
        console.warn('Alteração rejeitada - Unicode corrompido no JSON parseado:', alteration);
        corruptedAlterationsCount++;
        return false;
      }
    } catch (e) {
      // Se não conseguir fazer parse, deixar seguir (será tratado em outro lugar)
    }
  }

  return true;
};

// Função auxiliar para remover aspas desnecessárias de strings
const removeUnnecessaryQuotes = (value) => {
  // Se não for string, processar recursivamente se for objeto/array
  if (value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => removeUnnecessaryQuotes(item));
  }

  if (typeof value === 'object') {
    const cleaned = {};
    for (const key in value) {
      cleaned[key] = removeUnnecessaryQuotes(value[key]);
    }
    return cleaned;
  }

  if (typeof value === 'string') {
    // Remover aspas duplas no início e fim
    if (value.startsWith('"') && value.endsWith('"') && value.length > 1) {
      return value.slice(1, -1);
    }
    // Remover aspas simples no início e fim
    if (value.startsWith("'") && value.endsWith("'") && value.length > 1) {
      return value.slice(1, -1);
    }
  }

  return value;
};

const extractJsonField = (rawValue, field) => {
  if (typeof rawValue !== 'string') return null;
  const match = rawValue.match(new RegExp(`"${field}"\\s*:\\s*"([\\s\\S]*?)"\\s*(,|\\})`));
  if (!match) return null;
  return match[1].replace(/\\\\n/g, '\n').replace(/\\"/g, '"');
};

const parseJsonLikeObject = (rawValue) => {
  if (typeof rawValue !== 'string') return null;
  const trimmed = rawValue.trim();
  if (!trimmed.startsWith('{')) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  } catch (e) {
    // Fallback para extração manual
  }

  const name = extractJsonField(trimmed, 'name');
  const value = extractJsonField(trimmed, 'value');
  const description = extractJsonField(trimmed, 'description');

  if (name === null && value === null && description === null) {
    return null;
  }

  return {
    ...(name !== null ? { name } : {}),
    ...(value !== null ? { value } : {}),
    ...(description !== null ? { description } : {})
  };
};

const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }

  return JSON.stringify(value);
};

const normalizeComparableValue = (value, alteration) => {
  let normalized = value;

  if (alteration) {
    if (alteration.variable === 'voice') {
      const voiceValue = normalizeVoiceValue(normalized);
      if (voiceValue !== null) {
        normalized = voiceValue;
      }
    } else if (alteration.variable === 'flow' || alteration.variable === 'info.flow') {
      const flowValue = normalizeFlowValue(normalized);
      if (flowValue !== null) {
        normalized = flowValue;
      }
    } else if (alteration.variable === 'personality' || alteration.variable === 'info.personality') {
      const personalityValue = normalizePersonalityValue(normalized);
      if (personalityValue !== null) {
        normalized = personalityValue;
      }
    }
  }

  if (typeof normalized === 'string') {
    const trimmed = normalized.trim();
    const lower = trimmed.toLowerCase();

    if (lower === 'null') return null;
    if (lower === 'true') return true;
    if (lower === 'false') return false;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);

    return trimmed;
  }

  if (normalized && typeof normalized === 'object') {
    return stableStringify(normalized);
  }

  return normalized;
};

const deepCloneJson = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    return value;
  }
};

const normalizeStringValue = (value) => {
  if (typeof value !== 'string') return null;
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
};

const normalizeVoiceValue = (value) => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'string') {
    const normalized = normalizeStringValue(value);
    if (!normalized) return null;
    if (normalized === 'female' || normalized === 'feminina' || normalized === 'feminino') {
      return 'female';
    }
    if (normalized === 'male' || normalized === 'masculina' || normalized === 'masculino') {
      return 'male';
    }
    return null;
  }

  return null;
};

const normalizePersonalityValue = (value) => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') {
    return [1, 2, 3].includes(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = normalizeStringValue(value);
    if (!normalized) return null;
    if (normalized === '1' || normalized === 'amigavel' || normalized === 'amigável') return 1;
    if (normalized === '2' || normalized === 'profissional') return 2;
    if (normalized === '3' || normalized === 'consultiva') return 3;
  }

  return null;
};

const normalizeFlowValue = (value) => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') {
    return [1, 2, 3].includes(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = normalizeStringValue(value);
    if (!normalized) return null;
    if (normalized === '1' || normalized === 'qualificacao' || normalized === 'qualificação' ||
        normalized === 'qualificacao sdr' || normalized === 'qualificação sdr') {
      return 1;
    }
    if (normalized === '2' || normalized === 'vendas') return 2;
    if (normalized === '3' || normalized === 'agendamento') return 3;
  }

  return null;
};

const specialValueVariables = new Set([
  'voice',
  'personality',
  'info.personality',
  'flow',
  'info.flow'
]);

const getNormalizedSpecialValue = (alteration, valueOverride = undefined) => {
  if (!alteration || !specialValueVariables.has(alteration.variable)) {
    return undefined;
  }

  if (alteration.action && alteration.action !== 'alter') {
    return null;
  }

  const value = valueOverride !== undefined ? valueOverride : alteration.newValue;

  if (alteration.variable === 'voice') {
    return normalizeVoiceValue(value);
  }

  if (alteration.variable === 'flow' || alteration.variable === 'info.flow') {
    return normalizeFlowValue(value);
  }

  if (alteration.variable === 'personality' || alteration.variable === 'info.personality') {
    return normalizePersonalityValue(value);
  }

  return null;
};

const isValidSpecialValueAlteration = (alteration) => {
  if (!alteration || !specialValueVariables.has(alteration.variable)) {
    return true;
  }

  return getNormalizedSpecialValue(alteration) !== null;
};

const allowedPaths = [
  'name',
  'voice',
  'personality',
  'flow',
  'info.personality',
  'info.flow',
  'company_data.name',
  'company_data.cnpj',
  'company_data.role',
  'company_data.website',
  'company_data.description',
  'company_data.randomness',
  'company_data.audioEnabled',
  'company_data.messageGrouping',
  'company_data.humanRedirectConditions',
  'company_data.humanAvailabilityMessagingEnabled',
  'company_data.humanAvailabilityHours',
  'company_data.customImageInstructionsEnabled',
  'company_data.imagePrompt',
  'company_data.support',
  'company_data.support.email',
  'company_data.support.phone',
  'company_data.support.faqLink',
  'company_data.policies',
  'company_data.locations',
  'company_data.tagFields',
  'company_data.teamFields',
  'company_data.additionalInfo',
  'customer_data'
];

const allowedArrayPaths = new Set([
  'company_data.policies',
  'company_data.locations',
  'company_data.tagFields',
  'company_data.teamFields',
  'company_data.additionalInfo'
]);

const structuredValueVariables = new Set([
  'company_data.additionalInfo',
  'company_data.policies',
  'company_data.tagFields',
  'company_data.teamFields'
]);

// Função auxiliar para escapar conteúdo dentro de tags específicas
const escapeContentInMarkdown = (text, tag) => {
  const regex = new RegExp(`${tag}([^${tag}]+)${tag}`, 'g');
  return text.replace(regex, (match, content) => {
    return `${tag}${escapeHtml(content)}${tag}`;
  });
};

// Função para renderizar markdown como HTML de forma segura
const renderMarkdown = (markdown) => {
  if (!markdown) return '';

  // Primeiro, escapar conteúdo potencialmente perigoso dentro de elementos markdown
  // mas preservar a estrutura markdown para processamento
  let safeMarkdown = markdown;

  // Escapar conteúdo dentro de code blocks inline
  safeMarkdown = escapeContentInMarkdown(safeMarkdown, '`');

  // Escapar caracteres HTML perigosos fora de elementos markdown
  // Isso é feito de forma seletiva para não quebrar o markdown

  // Converter markdown básico para HTML
  let html = safeMarkdown
    // Headers
    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-bold mt-4 mb-2 text-slate-900 dark:text-slate-100">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3 text-slate-900 dark:text-slate-100">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-6 mb-4 text-slate-900 dark:text-slate-100">$1</h1>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900 dark:text-slate-100">$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^\* (.+)/gim, '<li class="ml-6 list-disc text-slate-700 dark:text-slate-300">$1</li>')
    .replace(/^\- (.+)/gim, '<li class="ml-6 list-disc text-slate-700 dark:text-slate-300">$1</li>')
    .replace(/^\d+\. (.+)/gim, '<li class="ml-6 list-decimal text-slate-700 dark:text-slate-300">$1</li>')
    // Code blocks
    .replace(/```(.*?)\n([\s\S]*?)```/g, '<pre class="bg-slate-100 dark:bg-slate-900 p-4 rounded-md overflow-x-auto my-4 text-slate-800 dark:text-slate-200"><code>$2</code></pre>')
    // Inline code - AQUI ESTÁ A CORREÇÃO PRINCIPAL
    .replace(/`(.+?)`/g, '<code class="bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-4 text-slate-700 dark:text-slate-300">')
    // Blockquotes
    .replace(/^> (.+)/gim, '<blockquote class="border-l-4 border-slate-300 dark:border-slate-600 pl-4 italic my-4 text-slate-600 dark:text-slate-400">$1</blockquote>')
    // Horizontal rules
    .replace(/^---$/gim, '<hr class="my-6 border-slate-300 dark:border-slate-600">');

  // Wrap in paragraphs if not already wrapped
  if (!html.startsWith('<')) {
    html = '<p class="mb-4 text-slate-700 dark:text-slate-300">' + html + '</p>';
  }

  // Wrap consecutive list items in ul/ol tags
  html = html.replace(/(<li class="ml-6 list-disc.*?<\/li>\n?)+/g, '<ul class="my-4">$&</ul>');
  html = html.replace(/(<li class="ml-6 list-decimal.*?<\/li>\n?)+/g, '<ol class="my-4">$&</ol>');

  return html;
};

// Loading message rotation functions
const startLoadingMessageRotation = () => {
  loadingMessageInterval = setInterval(() => {
    loadingMessageIndex.value = (loadingMessageIndex.value + 1) % loadingMessages.length;
  }, 5000); // Muda a cada 3 segundos
};

const stopLoadingMessageRotation = () => {
  if (loadingMessageInterval) {
    clearInterval(loadingMessageInterval);
    loadingMessageInterval = null;
  }
  loadingMessageIndex.value = 0;
};

// Função helper para resetar o estado do ajuste
const resetAdjustmentState = () => {
  showAdjustmentArea.value = false;
  adjustmentOptions.value = [];
  selectedOption.value = null;
  customPrompt.value = '';
  selectedMode.value = null;
};

// Função helper para resetar o estado do diagnóstico
const resetDiagnosticState = () => {
  showDiagnostic.value = false;
  diagnosticResult.value = '';
  diagnosticOriginalResult.value = '';
  diagnosticMappedResult.value = '';
  isDiagnosticLoading.value = false;
};

// Função helper para resetar todo o estado
const resetAllState = () => {
  showModeSelection.value = false;
  resetAdjustmentState();
  resetDiagnosticState();
};

// Methods
const handleAgentSelect = async (agentId, agent) => {
  selectedAgentId.value = agentId;
  selectedAgent.value = agent;

  if (agentId) {
    // Mostra a área de seleção de modo
    showModeSelection.value = true;
    // Reseta todo o resto
    resetAdjustmentState();
    resetDiagnosticState();
  } else {
    // Reseta tudo se não há agente selecionado
    resetAllState();
  }
};

const handleModeSelect = async (mode) => {
  selectedMode.value = mode;

  if (mode === 'guided') {
    // Opção A - vai para o fluxo normal de ajuste
    showModeSelection.value = false;
    showAdjustmentArea.value = true;
  } else if (mode === 'diagnostic') {
    // Opção B - executar diagnóstico
    showModeSelection.value = false;
    showDiagnostic.value = true;
    await executeDiagnostic();
  }
};

const startDiagnosticLoadingAnimation = () => {
  // Rotação de mensagens
  diagnosticMessageInterval = setInterval(() => {
    diagnosticMessageIndex.value = (diagnosticMessageIndex.value + 1) % diagnosticLoadingMessages.length;
  }, 3000); // Muda a cada 3 segundos
};

const stopDiagnosticLoadingAnimation = () => {
  if (diagnosticMessageInterval) {
    clearInterval(diagnosticMessageInterval);
    diagnosticMessageInterval = null;
  }
  diagnosticMessageIndex.value = 0;
};

const executeDiagnostic = async () => {
  isDiagnosticLoading.value = true;
  diagnosticResult.value = '';
  diagnosticOriginalResult.value = '';
  startDiagnosticLoadingAnimation();

  try {
    const headers = getAuthHeaders();
    const url = `/api/v1/agent_intelligent_adjustments/${selectedAgentId.value}/diagnostic`;

    const response = await axios.post(url, {}, { headers, withCredentials: true });

    if (response.data.success) {
      // Armazenar apenas a versão formatada para exibição
      diagnosticResult.value = response.data.result;
      diagnosticOriginalResult.value = response.data.original_result || '';
      diagnosticMappedResult.value = response.data.mapped_result || '';
    } else {
      throw new Error(response.data.error || 'Erro ao executar diagnóstico');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Erro ao executar diagnóstico. Por favor, tente novamente.';
    useAlert(errorMessage, { type: 'error' });
    // Voltar para a seleção de modo em caso de erro
    showDiagnostic.value = false;
    showModeSelection.value = true;
  } finally {
    isDiagnosticLoading.value = false;
    stopDiagnosticLoadingAnimation();
  }
};

const applyDiagnosticAdjustments = async () => {
  // Aplicar ajustes com o diagnóstico incluído
  step2Active.value = true;
  isSubmitting.value = true;

  try {
    const headers = getAuthHeaders();
    const url = `/api/v1/agent_intelligent_adjustments/${selectedAgentId.value}/apply`;

    // O diagnóstico original será recuperado da sessão no backend
    const requestData = {
      instruction_prompt: `Com base no diagnóstico realizado, aplique as melhorias necessárias no agente. Para campos obrigatórios ausentes, **NÃO** INSIRA VALORES FICTÍCIOS.`
      // Não precisa mais enviar o diagnóstico, será recuperado da sessão no backend
    };

    const response = await axios.post(url, requestData, { headers, withCredentials: true });

    if (response.data.success) {
      const rawResult = response.data.result;

      // Armazenar o JSON original completo para debug
      alterationsOriginalJson.value = deepCloneJson(rawResult);

      // Processar resultado (mesmo código do handleSubmit)
      adjustmentResult.value = rawResult;

      const {
        processedAlterations: processed,
        selectedAlterations: selected,
        stats
      } = prepareAlterationsForDisplay(rawResult?.alterations, { parseNewValueJson: false });

      processedAlterations.value = processed;
      selectedAlterations.value = selected;
      alterationProcessingStats.value = stats;

      // Alertar se houve alterações rejeitadas por Unicode corrompido
      if (corruptedAlterationsCount > 0) {
        useAlert(`${corruptedAlterationsCount} alteração(ões) rejeitada(s) devido a caracteres Unicode corrompidos.`, { type: 'warning' });
        corruptedAlterationsCount = 0; // Resetar contador
      }

      // Verificar e reportar automaticamente se houver problemas no tratamento
      await checkAndReportTreatmentIssues();

      // Esconder diagnóstico e mostrar alterações
      showDiagnostic.value = false;
      showAlterations.value = true;

      // Scroll to top
      const scrollableParent = document.querySelector('.flex-1.overflow-y-auto');
      if (scrollableParent) {
        scrollableParent.scrollTo({ top: 0 });
      }
    } else {
      throw new Error('Resposta da API não foi bem-sucedida');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Erro ao aplicar ajustes. Por favor, tente novamente.';
    useAlert(errorMessage, { type: 'error' });
  } finally {
    isSubmitting.value = false;
  }
};

const fetchAdjustmentOptions = async () => {
  isLoading.value = true;
  loadingMessageIndex.value = 0;
  startLoadingMessageRotation();
  
  try {
    const headers = getAuthHeaders();
    const url = `/api/v1/agent_intelligent_adjustments/${selectedAgentId.value}/options`;
    
    //.log('🔄 Buscando opções de ajuste:');
    //console.log('📤 URL:', url);
    //console.log('📤 Headers:', headers);
    //console.log('📤 Agent ID:', selectedAgentId.value);
    
    const response = await axios.get(url, { headers, withCredentials: true });
    
    //console.log('📥 Resposta completa:', response);
    //console.log('📥 Status:', response.status);
    //console.log('📥 Data recebida:', response.data);
    
    if (response.data.variations) {
      adjustmentOptions.value = response.data.variations;
      //console.log('✅ Opções de ajuste carregadas:', adjustmentOptions.value);
      //console.log('✅ Quantidade de opções:', adjustmentOptions.value.length);
      //console.log('✅ Primeira opção:', adjustmentOptions.value[0]);
    } else {
      //console.error('❌ API não retornou variations');
      throw new Error('Resposta da API não contém variations');
    }
    
  } catch (error) {
    //console.error('❌ Erro ao buscar opções de ajuste:', error);
    //console.error('❌ Error response:', error.response);
    //console.error('❌ Error data:', error.response?.data);
    
    // Tratamento específico para erro de timeout
    const errorMessage = error.response?.data?.error || '';
    if (errorMessage.includes('Timeout') || errorMessage.includes('timeout')) {
      useAlert('O sistema está demorando mais que o esperado. Por favor, tente novamente em alguns instantes.', { 
        type: 'warning',
        duration: 5000 
      });
      
      // Retry automático após 2 segundos
      setTimeout(async () => {
        //console.log('Tentando novamente após timeout...');
        isLoading.value = true;
        try {
          const response = await axios.get(url, { headers, withCredentials: true });
          if (response.data.variations) {
            adjustmentOptions.value = response.data.variations;
            //console.log('✅ Opções carregadas na segunda tentativa');
            //useAlert('Ajustes carregados com sucesso!', { type: 'success' });
          }
        } catch (retryError) {
          //console.error('❌ Erro na segunda tentativa:', retryError);
          //useAlert('Não foi possível carregar as opções de ajuste. Por favor, tente novamente mais tarde.', { 
          //  type: 'error' 
          //});
        } finally {
          isLoading.value = false;
        }
      }, 2000);
      
      return; // Não desativa o loading ainda, pois vai tentar novamente
    } else {
      // Outros erros
      //useAlert('Erro ao carregar opções de ajuste. Por favor, tente novamente.', { type: 'error' });
    }
  }
  
  isLoading.value = false;
  stopLoadingMessageRotation();
};

const handleBlockSelect = (option) => {
  selectedOption.value = option;
  customPrompt.value = ''; // Limpar prompt customizado ao selecionar bloco
};

const handleCustomPromptChange = (prompt) => {
  customPrompt.value = prompt;
  selectedOption.value = null; // Limpar seleção de bloco ao digitar prompt
};

const toggleAlteration = (alteration) => {
  const index = selectedAlterations.value.findIndex(
    alt => alt.variable === alteration.variable && alt.itemName === alteration.itemName
  );
  
  if (index >= 0) {
    selectedAlterations.value.splice(index, 1);
  } else {
    selectedAlterations.value.push(alteration);
  }
};

const isAlterationSelected = (alteration) => {
  return selectedAlterations.value.some(
    alt => alt.variable === alteration.variable && alt.itemName === alteration.itemName
  );
};

const getParsedAgentData = () => {
  if (!selectedAgent.value) return null;

  try {
    return {
      ...selectedAgent.value,
      company_data: typeof selectedAgent.value.company_data === 'string'
        ? JSON.parse(selectedAgent.value.company_data)
        : selectedAgent.value.company_data,
      customer_data: typeof selectedAgent.value.customer_data === 'string'
        ? JSON.parse(selectedAgent.value.customer_data)
        : selectedAgent.value.customer_data
    };
  } catch (e) {
    return null;
  }
};

const getCurrentValue = (alteration, agentDataOverride = null) => {
  const agentData = agentDataOverride || getParsedAgentData();
  if (!agentData) return null;

  try {
    // Tratar campos que vêm com prefixo 'info.'
    let variablePath = alteration.variable;
    if (variablePath.startsWith('info.')) {
      // Remover o prefixo 'info.' pois esses campos estão no nível raiz do agente
      variablePath = variablePath.substring(5); // Remove 'info.'
    }
    
    // Navegar pelo caminho da variável
    const path = variablePath.split('.');
    let current = agentData;
    
    for (const key of path) {
      if (current && typeof current === 'object') {
        current = current[key];
      } else {
        return null;
      }
    }
    
    // Se for um array e temos itemName, procurar o item específico
    if (Array.isArray(current) && alteration.itemName) {
      const item = current.find(item => {
        // Para locations, comparar pelo CEP
        if (alteration.variable.includes('locations') && item.cep) {
          return item.cep === alteration.itemName;
        }
        
        // Para tags e times, fazer comparação case-insensitive e considerando normalização
        if (alteration.variable === 'company_data.tagFields') {
          // Tags: comparar considerando minúsculas e hífens
          const normalizedItemName = alteration.itemName.toLowerCase().replace(/\s+/g, '-');
          const normalizedCurrentName = (item.name || '').toLowerCase().replace(/\s+/g, '-');
          return normalizedCurrentName === normalizedItemName;
        }
        
        if (alteration.variable === 'company_data.teamFields') {
          // Times: comparar apenas minúsculas
          return (item.name || '').toLowerCase() === alteration.itemName.toLowerCase();
        }
        
        // Para outros arrays, comparar por name ou key
        return item.name === alteration.itemName || 
               item.key === alteration.itemName;
      });
      return item || null;
    }
    
    // Mapeamento de valores especiais
    if (variablePath === 'flow') {
      // Converter string para número se necessário
      const flowValue = typeof current === 'string' ? parseInt(current) : current;
      const flowMap = {
        1: 'Qualificacao SDR',
        2: 'Vendas',
        3: 'Agendamento'
      };
      return flowMap[flowValue] || flowValue || null;
    }
    
    if (variablePath === 'personality') {
      // Converter string para número se necessário
      const personalityValue = typeof current === 'string' ? parseInt(current) : current;
      const personalityMap = {
        1: 'amigavel',
        2: 'profissional',
        3: 'consultiva'
      };
      return personalityMap[personalityValue] || personalityValue || null;
    }
    
    return current;
  } catch (e) {
    //console.error('Erro ao buscar valor atual:', e);
    return null;
  }
};

const filterMissingCurrentAlterations = (alterations, options = {}) => {
  if (!Array.isArray(alterations)) return [];

  const { agentData = null, stats = null } = options;
  const resolvedAgentData = agentData || getParsedAgentData();

  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
      const trimmed = value.trim().toLowerCase();
      return trimmed === '' || trimmed === 'null';
    }
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  };

  if (!resolvedAgentData) {
    return alterations.filter(alteration => {
      if (!alteration) {
        return false;
      }

      if (alteration.action === 'remove') {
        if (stats) {
          stats.missingCurrent += 1;
        }
        return false;
      }

      if (alteration.action === 'alter' && alteration.itemName) {
        if (stats) {
          stats.missingCurrent += 1;
        }
        return false;
      }

      return true;
    });
  }

  return alterations.filter(alteration => {
    if (!alteration) {
      return false;
    }

    if (alteration.action === 'remove' && !alteration.itemName) {
      const currentValue = getCurrentValue(alteration, resolvedAgentData);
      const exists = !isEmptyValue(currentValue);

      if (!exists && stats) {
        stats.missingCurrent += 1;
      }

      return exists;
    }

    if (!alteration.itemName) {
      return true;
    }

    if (alteration.action !== 'alter' && alteration.action !== 'remove') {
      return true;
    }

    const currentValue = getCurrentValue(alteration, resolvedAgentData);
    const exists = currentValue !== null && currentValue !== undefined;

    if (!exists && stats) {
      stats.missingCurrent += 1;
    }

    return exists;
  });
};

const filterNoOpAlterations = (alterations, options = {}) => {
  if (!Array.isArray(alterations)) return [];

  const { agentData = null, stats = null } = options;
  const resolvedAgentData = agentData || getParsedAgentData();

  if (!resolvedAgentData) {
    return alterations;
  }

  return alterations.filter(alteration => {
    if (!alteration || alteration.action !== 'alter') {
      return true;
    }

    const currentValue = getCurrentValue(alteration, resolvedAgentData);
    const normalizedCurrent = normalizeComparableValue(currentValue, alteration);
    const normalizedNew = normalizeComparableValue(alteration.newValue, alteration);

    const isSame = normalizedCurrent === normalizedNew;

    if (isSame && stats) {
      stats.noOp += 1;
    }

    return !isSame;
  });
};

const normalizeAlteration = (alteration, options = {}) => {
  const { parseNewValueJson = false } = options;

  if (!alteration || typeof alteration !== 'object') {
    return alteration;
  }

  if (typeof alteration.newValue === 'string') {
    const trimmedNewValue = alteration.newValue.trim();

    if (parseNewValueJson &&
        (trimmedNewValue.startsWith('{') || trimmedNewValue.startsWith('['))) {
      try {
        alteration.newValue = JSON.parse(trimmedNewValue);
      } catch (e) {
        // Se falhar o parse, manter como string
      }
    }

    if (structuredValueVariables.has(alteration.variable) &&
        trimmedNewValue.startsWith('{')) {
      const parsedObject = parseJsonLikeObject(trimmedNewValue);
      if (parsedObject) {
        alteration.newValue = parsedObject;
      }
    }
  }

  alteration.newValue = removeUnnecessaryQuotes(alteration.newValue);
  if (alteration.currentValue !== undefined) {
    alteration.currentValue = removeUnnecessaryQuotes(alteration.currentValue);
  }

  if (structuredValueVariables.has(alteration.variable) &&
      typeof alteration.newValue === 'string' &&
      alteration.itemName) {
    if (alteration.variable === 'company_data.additionalInfo' ||
        alteration.variable === 'company_data.policies') {
      alteration.newValue = {
        name: alteration.itemName,
        value: alteration.newValue
      };
    } else if (alteration.variable === 'company_data.tagFields' ||
               alteration.variable === 'company_data.teamFields') {
      alteration.newValue = {
        name: alteration.itemName,
        description: alteration.newValue
      };
    }
  }

  if (alteration.variable === 'company_data.tagFields') {
    if (alteration.newValue && typeof alteration.newValue === 'object' && alteration.newValue.name) {
      alteration.newValue.name = alteration.newValue.name.toLowerCase().replace(/\s+/g, '-');
    }
    if (alteration.itemName) {
      alteration.itemName = alteration.itemName.toLowerCase().replace(/\s+/g, '-');
    }
  } else if (alteration.variable === 'company_data.teamFields') {
    if (alteration.newValue && typeof alteration.newValue === 'object' && alteration.newValue.name) {
      alteration.newValue.name = alteration.newValue.name.toLowerCase();
    }
    if (alteration.itemName) {
      alteration.itemName = alteration.itemName.toLowerCase();
    }
  }

  return alteration;
};

const isAllowedAlteration = (alteration) => {
  if (!alteration || !alteration.variable) return false;

  if (allowedPaths.includes(alteration.variable)) {
    return true;
  }

  if (alteration.itemName && allowedArrayPaths.has(alteration.variable)) {
    return true;
  }

  return false;
};

const prepareAlterationsForDisplay = (alterations, options = {}) => {
  const stats = {
    total: Array.isArray(alterations) ? alterations.length : 0,
    invalid: 0,
    disallowed: 0,
    invalidValue: 0,
    missingCurrent: 0,
    noOp: 0
  };

  if (!Array.isArray(alterations) || alterations.length === 0) {
    return {
      processedAlterations: [],
      selectedAlterations: [],
      stats
    };
  }

  const normalizedAlterations = deepCloneJson(alterations).map(alt => normalizeAlteration(alt, options));

  const allowedAlterations = normalizedAlterations.filter(alt => {
    if (!alt || typeof alt !== 'object') {
      stats.invalid += 1;
      return false;
    }

    if (!isValidAlteration(alt)) {
      stats.invalid += 1;
      return false;
    }

    if (!isAllowedAlteration(alt)) {
      stats.disallowed += 1;
      return false;
    }

    if (!isValidSpecialValueAlteration(alt)) {
      stats.invalidValue += 1;
      return false;
    }

    return true;
  });

  const agentData = getParsedAgentData();
  const withExistingCurrent = filterMissingCurrentAlterations(allowedAlterations, { agentData, stats });
  const processedAlterations = filterNoOpAlterations(withExistingCurrent, { agentData, stats });

  return {
    processedAlterations,
    selectedAlterations: [...processedAlterations],
    stats
  };
};

const normalizeAlterationForSave = (alteration) => {
  if (!alteration || typeof alteration !== 'object') {
    return null;
  }

  const normalizedSpecial = getNormalizedSpecialValue(alteration);
  if (normalizedSpecial === null) {
    return null;
  }

  if (normalizedSpecial !== undefined) {
    return { ...alteration, newValue: normalizedSpecial };
  }

  return { ...alteration };
};

const resetForm = () => {
  showAlterations.value = false;
  adjustmentResult.value = null;
  processedAlterations.value = [];
  selectedAlterations.value = [];
  alterationProcessingStats.value = null;
  selectedAgentId.value = null;
  selectedAgent.value = null;
  showAdjustmentArea.value = false;
  showModeSelection.value = false;
  showDiagnostic.value = false;
  diagnosticResult.value = '';
  isDiagnosticLoading.value = false;
  selectedMode.value = null;
  adjustmentOptions.value = [];
  selectedOption.value = null;
  customPrompt.value = '';

  // Reset debug states for alterations
  showAlterationsDebug.value = false;
  alterationsOriginalJson.value = null;
  step2Active.value = false;
  step3Active.value = false;

  // Reset controles de reports
  reportsSent.value = {
    treatment: false,
    client: false
  };
  // showInitialPage.value = true; // Removido - agora sempre vai direto para seleção
};

// Função removida - não é mais necessária pois agora vai direto para seleção do agente

const handleSaveAlterations = async () => {
  const normalizedAlterations = selectedAlterations.value
    .map(alteration => normalizeAlterationForSave(alteration))
    .filter(Boolean);

  if (normalizedAlterations.length === 0) {
    useAlert('Por favor, selecione pelo menos uma alteração para aplicar', { type: 'warning' });
    return;
  }

  // Ativar passo 3 assim que clica em "Aplicar Alterações Selecionadas"
  step3Active.value = true;
  isSubmitting.value = true;
  
  try {
    const headers = getAuthHeaders();
    const url = `/api/v1/agent_intelligent_adjustments/${selectedAgentId.value}/save_alterations`;
    
    const requestData = {
      alterations: normalizedAlterations
    };
    
    //console.log('🔄 Salvando alterações selecionadas:');
    //console.log('📤 URL:', url);
    //console.log('📤 Agent ID:', selectedAgentId.value);
    //console.log('📤 Alterações originais:', selectedAlterations.value);
    //console.log('📤 Alterações normalizadas:', normalizedAlterations);
    
    const response = await axios.post(url, requestData, { headers, withCredentials: true });
    
    //console.log('📥 Resposta:', response.data);
    
    if (response.data.success) {
      useAlert('Alterações aplicadas com sucesso ao agente!', { type: 'success' });
      
      // Scroll to top quando finalizar passo 3
      const scrollableParent = document.querySelector('.flex-1.overflow-y-auto');
      if (scrollableParent) {
        scrollableParent.scrollTo({ top: 0 /* , behavior: 'smooth' */ });
      }
      
      // Redirecionar para a listagem de agentes com o modal aberto
      setTimeout(() => {
        router.push({
          name: 'agentis_list',
          query: { 
            openAgent: selectedAgentId.value 
          }
        });
      }, 1500);
    } else {
      throw new Error(response.data.error || 'Erro ao aplicar alterações');
    }
  } catch (error) {
    //console.error('❌ Erro ao salvar alterações:', error);
    const errorMessage = error.response?.data?.error || 'Erro ao salvar alterações. Por favor, tente novamente.';
    useAlert(errorMessage, { type: 'error' });
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (!isFormValid.value) {
    useAlert('Por favor, selecione uma opção ou digite um prompt', { type: 'error' });
    return;
  }

  // Ativar passo 2 assim que clica em "Aplicar Ajuste"
  step2Active.value = true;
  isSubmitting.value = true;
  
  try {
    const headers = getAuthHeaders();
    const url = `/api/v1/agent_intelligent_adjustments/${selectedAgentId.value}/apply`;
    
    // Enviar o prompt no formato esperado pelo backend
    const requestData = {
      instruction_prompt: selectedOption.value ? selectedOption.value.prompt : customPrompt.value
    };
    
    //console.log('🔄 Aplicando ajuste inteligente:');
    //console.log('📤 URL:', url);
    //console.log('📤 Headers:', headers);
    //console.log('📤 Agent ID:', selectedAgentId.value);
    //console.log('📤 Dados enviados:', requestData);
    //console.log('📤 Tipo de ajuste:', selectedOption.value ? 'Opção pré-definida' : 'Prompt customizado');
    if (selectedOption.value) {
      //console.log('📤 Opção selecionada:', selectedOption.value);
    }
    
    const response = await axios.post(url, requestData, { headers, withCredentials: true });
    
    //console.log('📥 Resposta completa:', response);
    //console.log('📥 Status:', response.status);
    //console.log('📥 Data recebida:', response.data);
    
    if (response.data.success) {
      //console.log('✅ Ajuste aplicado com sucesso!');
      //console.log('📊 Resultado do ajuste:', response.data.result);

      const rawResult = response.data.result;

      // Armazenar o JSON original completo para debug
      alterationsOriginalJson.value = deepCloneJson(rawResult);

      // Armazenar o resultado para exibição
      adjustmentResult.value = rawResult;

      const {
        processedAlterations: processed,
        selectedAlterations: selected,
        stats
      } = prepareAlterationsForDisplay(rawResult?.alterations, { parseNewValueJson: false });

      processedAlterations.value = processed;
      selectedAlterations.value = selected;
      alterationProcessingStats.value = stats;

      // Alertar se houve alterações rejeitadas por Unicode corrompido
      if (corruptedAlterationsCount > 0) {
        useAlert(`${corruptedAlterationsCount} alteração(ões) rejeitada(s) devido a caracteres Unicode corrompidos.`, { type: 'warning' });
        corruptedAlterationsCount = 0; // Resetar contador
      }

      // Verificar e reportar automaticamente se houver problemas no tratamento
      await checkAndReportTreatmentIssues();

      // Mostrar alterações na página
      showAlterations.value = true;
      showAdjustmentArea.value = false;
      
      // Scroll to top quando carregar o conteúdo do passo 2
      const scrollableParent = document.querySelector('.flex-1.overflow-y-auto');
      if (scrollableParent) {
        scrollableParent.scrollTo({ top: 0 /* , behavior: 'smooth' */ });
      }
      
      // Não resetar o formulário automaticamente para permitir visualização do resultado
    } else {
      //console.error('❌ API retornou success: false');
      throw new Error('Resposta da API não foi bem-sucedida');
    }
    
  } catch (error) {
    //console.error('❌ Erro ao aplicar ajuste:', error);
    //console.error('❌ Error response:', error.response);
    //console.error('❌ Error data:', error.response?.data);
    //console.error('❌ Error status:', error.response?.status);
    const errorMessage = error.response?.data?.error || 'Erro ao aplicar ajuste. Por favor, tente novamente.';
    useAlert(errorMessage, { type: 'error' });
  } finally {
    isSubmitting.value = false;
  }
};

// Debug console methods
const checkDebugMode = async () => {
  try {
    // Check if current user has debug permissions
    const headers = getAuthHeaders();
    const response = await axios.get('/api/v1/check_debug_mode', { headers, withCredentials: true });
    const hasDebugPermission = response.data.debug_mode === true;
    
    // Enable debug based on user permissions
    isDebugMode.value = hasDebugPermission;
    
    // Set the reason for debug mode
    if (hasDebugPermission) {
      debugModeReason.value = 'Debug Mode Enabled';
    } else {
      debugModeReason.value = '';
    }
    
    //console.log('Debug mode check:', {
    //  hasDebugPermission,
    //  finalDebugMode: isDebugMode.value,
    //  reason: debugModeReason.value
    //});
  } catch (error) {
    //console.error('Error checking debug mode:', error);
    // If API fails, debug mode stays disabled
    isDebugMode.value = false;
  }
};

const toggleDebugConsole = () => {
  if (isDebugMode.value) {
    showDebugConsole.value = !showDebugConsole.value;
    if (showDebugConsole.value && selectedAgent.value) {
      // Populate debug console with current agent data
      debugSelectedAgentId.value = selectedAgentId.value;
      loadDebugAgentData();
    }
  }
};

const loadDebugAgentData = async () => {
  if (!selectedAgentId.value || !selectedAgent.value) {
    // Se já temos o agente selecionado, use os dados existentes
    if (selectedAgent.value) {
      try {
        // Prepare agent data using the existing method
        const agentData = prepareAgentDataForDebug(selectedAgent.value);
        debugAgentData.value = agentData;
        generateDebugFormattedOutput();
      } catch (error) {
        //console.error('Error preparing debug data:', error);
      }
    }
    return;
  }
  
  try {
    const headers = getAuthHeaders();
    const url = `/api/v1/agent_intelligent_adjustments/${selectedAgentId.value}/debug_data`;
    
    const response = await axios.get(url, { headers, withCredentials: true });
    debugAgentData.value = response.data;
    generateDebugFormattedOutput();
  } catch (error) {
    //console.error('Error loading debug data:', error);
    // Fallback: use local data if available
    if (selectedAgent.value) {
      const agentData = prepareAgentDataForDebug(selectedAgent.value);
      debugAgentData.value = agentData;
      generateDebugFormattedOutput();
    }
  }
};

// Helper function to prepare agent data locally
const prepareAgentDataForDebug = (agent) => {
  if (!agent) return null;
  
  // This mimics what the backend does
  const companyData = typeof agent.company_data === 'string' 
    ? JSON.parse(agent.company_data) 
    : agent.company_data || {};
    
  const customerData = typeof agent.customer_data === 'string'
    ? JSON.parse(agent.customer_data)
    : agent.customer_data || [];
    
  const personalityMap = {
    1: 'amigavel',
    2: 'profissional',
    3: 'consultiva'
  };
  
  const flowMap = {
    1: 'Qualificacao SDR',
    2: 'Vendas',
    3: 'Agendamento'
  };
  
  return {
    Agente: {
      name: agent.name || '',
      voice: agent.voice || '',
      company_data: companyData,
      customer_data: customerData
    },
    info: {
      personality: personalityMap[agent.personality] || '',
      flow: flowMap[agent.flow] || ''
    }
  };
};

const generateDebugFormattedOutput = () => {
  if (!debugAgentData.value) {
    debugFormattedOutput.value = '';
    return;
  }

  // Montar JSON com instruction_prompt + dados do agente (sem wrapper agent_data)
  const output = {
    instruction_prompt: debugCustomPrompt.value || '',
    ...debugAgentData.value
  };

  debugFormattedOutput.value = JSON.stringify(output, null, 2);
};

const copyDebugToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(debugFormattedOutput.value);
    useAlert('Copiado para a área de transferência', { type: 'success' });
  } catch (error) {
    //console.error('Error copying to clipboard:', error);
    useAlert('Erro ao copiar para a área de transferência', { type: 'error' });
  }
};

const copyAlterationsJsonToClipboard = async () => {
  try {
    const jsonString = JSON.stringify(alterationsOriginalJson.value, null, 2);
    await navigator.clipboard.writeText(jsonString);
    useAlert('JSON copiado para a área de transferência', { type: 'success' });
  } catch (error) {
    //console.error('Error copying to clipboard:', error);
    useAlert('Erro ao copiar JSON para a área de transferência', { type: 'error' });
  }
};

// Função genérica para enviar reports ao ClickUp
const sendReportToClickUp = async (reportType) => {
  try {
    const headers = getAuthHeaders();

    // Enviar para o backend que fará a chamada ao ClickUp
    const response = await axios.post('/api/v1/clickup/send_to_channel', {
      channel: 'adjustments_report',
      report_type: reportType === 'treatment' ? 'TREATMENT' : 'CLIENT',
      original_data: alterationsOriginalJson.value,
      processed_data: processedAlterations.value,
      account_id: selectedAgent.value?.account_id,
      agent_data: selectedAgent.value
    }, { headers });

    if (response.status === 200 || response.status === 201) {
      // Marcar como enviado
      if (reportType === 'treatment') {
        reportsSent.value.treatment = true;
      } else {
        reportsSent.value.client = true;
      }

      // Só mostrar alerta se for report manual do cliente
      if (reportType === 'client') {
        useAlert('Problema reportado com sucesso!', { type: 'success' });
      }

      return true;
    } else {
      throw new Error('Falha ao reportar');
    }
  } catch (error) {
    console.error(`Erro ao reportar problema (${reportType}):`, error);

    // Se for report do cliente e falhar, mostrar erro
    if (reportType === 'client') {
      try {
        const fallbackData = {
          original: alterationsOriginalJson.value,
          tratado: processedAlterations.value
        };

        await navigator.clipboard.writeText(JSON.stringify(fallbackData, null, 2));
        useAlert('Erro ao enviar. Dados copiados para área de transferência.', { type: 'warning' });
      } catch (clipboardError) {
        useAlert('Erro ao reportar problema.', { type: 'error' });
      }
    }

    return false;
  }
};

// Função para report automático quando há problemas no tratamento
const checkAndReportTreatmentIssues = async () => {
  // Verificar se já foi enviado report de tratamento
  if (reportsSent.value.treatment) {
    return;
  }

  if (alterationProcessingStats.value) {
    const filteredForReport =
      (alterationProcessingStats.value.invalid || 0) +
      (alterationProcessingStats.value.disallowed || 0) +
      (alterationProcessingStats.value.invalidValue || 0);

    if (alterationProcessingStats.value.total > 0 && filteredForReport > 0) {
      await sendReportToClickUp('treatment');
    }

    return;
  }

  // Fallback para o comportamento anterior caso estatísticas não estejam disponíveis
  if (alterationsOriginalJson.value && alterationsOriginalJson.value.alterations) {
    const originalCount = alterationsOriginalJson.value.alterations.length;
    const processedCount = processedAlterations.value.length;

    if (originalCount > processedCount && processedCount >= 0) {
      await sendReportToClickUp('treatment');
    }
  }
};

// Função para report manual do cliente
const reportAdjustmentIssue = async () => {
  // Verificar se o cliente já reportou
  if (reportsSent.value.client) {
    useAlert('Você já reportou este problema anteriormente.', { type: 'info' });
    return;
  }

  // Enviar report do cliente
  await sendReportToClickUp('client');
};

const pasteAndPopulateFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText();
    if (!text.trim()) {
      useAlert('Área de transferência vazia', { type: 'warning' });
      return;
    }

    // Parse do JSON
    const parsed = JSON.parse(text);

    // Extrair instruction_prompt se existir
    if (parsed.instruction_prompt) {
      debugCustomPrompt.value = parsed.instruction_prompt;
    }

    // Atualizar output
    generateDebugFormattedOutput();
    useAlert('JSON colado e campos populados com sucesso', { type: 'success' });
  } catch (error) {
    useAlert('Erro ao processar JSON da área de transferência', { type: 'error' });
  }
};

const applyDebugAdjustment = async () => {
  if (!debugCustomPrompt.value.trim() || !selectedAgent.value) {
    useAlert('Por favor, preencha o prompt de instrução', { type: 'warning' });
    return;
  }

  step2Active.value = true;
  isSubmitting.value = true;

  try {
    const headers = getAuthHeaders();
    const url = `/api/v1/agent_intelligent_adjustments/${selectedAgentId.value}/apply`;

    const requestData = {
      instruction_prompt: debugCustomPrompt.value.trim()
    };

    const response = await axios.post(url, requestData, { headers, withCredentials: true });

    if (response.data.success) {
      const rawResult = response.data.result;

      // Armazenar o JSON original completo para debug
      alterationsOriginalJson.value = deepCloneJson(rawResult);

      // Armazenar o resultado para exibição
      adjustmentResult.value = rawResult;

      const {
        processedAlterations: processed,
        selectedAlterations: selected,
        stats
      } = prepareAlterationsForDisplay(rawResult?.alterations, { parseNewValueJson: false });

      processedAlterations.value = processed;
      selectedAlterations.value = selected;
      alterationProcessingStats.value = stats;

      // Alertar se houve alterações rejeitadas por Unicode corrompido
      if (corruptedAlterationsCount > 0) {
        useAlert(`${corruptedAlterationsCount} alteração(ões) rejeitada(s) devido a caracteres Unicode corrompidos.`, { type: 'warning' });
        corruptedAlterationsCount = 0; // Resetar contador
      }

      // Verificar e reportar automaticamente se houver problemas no tratamento
      await checkAndReportTreatmentIssues();

      // Fechar debug console e mostrar alterações
      showDebugConsole.value = false;
      showAlterations.value = true;
      showAdjustmentArea.value = false;
      // showInitialPage.value = false; // Não é mais necessário

      // Scroll to top
      const scrollableParent = document.querySelector('.flex-1.overflow-y-auto');
      if (scrollableParent) {
        scrollableParent.scrollTo({ top: 0 });
      }

      useAlert('Ajustes aplicados com sucesso!', { type: 'success' });
    } else {
      throw new Error('Resposta da API não foi bem-sucedida');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Erro ao aplicar ajuste. Por favor, tente novamente.';
    useAlert(errorMessage, { type: 'error' });
  } finally {
    isSubmitting.value = false;
  }
};


// Process manual OpenAI response
const processDebugManualResponse = async () => {
  if (!debugManualResponse.value.trim() || !selectedAgent.value) {
    useAlert('Por favor, cole uma resposta válida da OpenAI e selecione um agente', { type: 'error' });
    return;
  }
  
  try {
    // Parse the manual response
    let parsedResponse;
    try {
      parsedResponse = JSON.parse(debugManualResponse.value);
    } catch (e) {
      // Try to extract JSON from text
      const jsonMatch = debugManualResponse.value.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        parsedResponse = JSON.parse(jsonMatch[0]);
      } else {
        throw new Error('Formato inválido de resposta');
      }
    }
    
    const rawResponse = parsedResponse;

    // Armazenar o JSON original completo para debug
    alterationsOriginalJson.value = deepCloneJson(rawResponse);

    // Simulate the same processing as the real API response
    adjustmentResult.value = rawResponse;

    const {
      processedAlterations: processed,
      selectedAlterations: selected,
      stats
    } = prepareAlterationsForDisplay(rawResponse?.alterations, { parseNewValueJson: true });

    processedAlterations.value = processed;
    selectedAlterations.value = selected;
    alterationProcessingStats.value = stats;

    // Alertar se houve alterações rejeitadas por Unicode corrompido
    if (corruptedAlterationsCount > 0) {
      useAlert(`${corruptedAlterationsCount} alteração(ões) rejeitada(s) devido a caracteres Unicode corrompidos.`, { type: 'warning' });
      corruptedAlterationsCount = 0; // Resetar contador
    }

    // Verificar e reportar automaticamente se houver problemas no tratamento
    await checkAndReportTreatmentIssues();

    // Show alterations page
    showAlterations.value = true;
    showAdjustmentArea.value = false;
    // showInitialPage.value = false; // Não é mais necessário
    step2Active.value = true;
    
    // Close debug console
    showDebugConsole.value = false;
    debugManualResponse.value = '';
    showDebugResponseInput.value = false;
    
    // Scroll to top
    const scrollableParent = document.querySelector('.flex-1.overflow-y-auto');
    if (scrollableParent) {
      scrollableParent.scrollTo({ top: 0 });
    }
    
    useAlert('Resposta manual processada com sucesso!', { type: 'success' });
    
  } catch (error) {
    //console.error('Erro ao processar resposta manual:', error);
    useAlert('Erro ao processar resposta. Verifique o formato JSON.', { type: 'error' });
  }
};

// Watch debug prompt changes
watch(debugCustomPrompt, () => {
  generateDebugFormattedOutput();
});

// Watch selected agent changes
watch(selectedAgent, () => {
  if (showDebugConsole.value && selectedAgent.value) {
    debugSelectedAgentId.value = selectedAgentId.value;
    loadDebugAgentData();
  }
});

// Para armazenar o ResizeObserver
let resizeObserver = null;

// Removido onBeforeUnmount - já temos onUnmounted fazendo a limpeza

// Funções para buscar labels e teams do sistema
const fetchSystemLabels = async () => {
  try {
    await store.dispatch('labels/get');
    systemLabels.value = store.getters['labels/getLabels'] || [];
  } catch (error) {
    //console.error('Erro ao buscar labels do sistema:', error);
  }
};

const fetchSystemTeams = async () => {
  try {
    await store.dispatch('teams/get');
    systemTeams.value = store.getters['teams/getTeams'] || [];
  } catch (error) {
    //console.error('Erro ao buscar teams do sistema:', error);
  }
};

onMounted(() => {
  //console.log('AgentIntelligentAdjustments montado');
  checkDebugMode();
  
  // Buscar labels e teams do sistema ao montar o componente
  fetchSystemLabels();
  fetchSystemTeams();
  
  nextTick(() => {
    checkScrollbar();
    
    // Observar mudanças no tamanho do conteúdo
    if (scrollContainer.value) {
      resizeObserver = new ResizeObserver(() => {
        checkScrollbar();
      });
      resizeObserver.observe(scrollContainer.value);
    }
  });
});

// Cleanup on unmount
onUnmounted(() => {
  stopLoadingMessageRotation();
  stopDiagnosticLoadingAnimation();
  // Limpar o ResizeObserver se existir
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

// Ref para o elemento com scroll
const scrollContainer = ref(null);

// Função para verificar e ajustar padding quando há scrollbar
const checkScrollbar = () => {
  if (scrollContainer.value) {
    const hasScrollbar = scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight;
    if (hasScrollbar) {
      scrollContainer.value.style.paddingLeft = '10px';
    } else {
      scrollContainer.value.style.paddingLeft = '0';
    }
  }
};
</script>

<template>
  <div class="w-full h-full mx-auto bg-n-background flex flex-col">
    <!-- Header -->
    <header
      class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-n-background border-b border-n-weak pl-6 flex-shrink-0">
      <div class="flex justify- items-center gap-2">
        <woot-sidemenu-icon v-if="showSidemenuIcon" />
        <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
          Ajustes Inteligentes de Agente
        </h1>
      </div>
      
      <!-- Debug button (only visible in debug mode) -->
      <div v-if="isDebugMode" class="flex items-center gap-2">
        <Button
          @click="toggleDebugConsole"
          size="sm"
          variant="outline"
          color="amber"
          :label="showDebugConsole ? 'Fechar Debug' : 'Abrir Debug Console'"
        >
          <template #icon>
            <i :class="showDebugConsole ? 'i-lucide-x' : 'i-lucide-terminal'"></i>
          </template>
        </Button>
      </div>
    </header>
    
    <!-- Debug Console (only visible when toggled) -->
    <div v-if="showDebugConsole && isDebugMode" class="debug-console-container">
      <div class="debug-console">
        <div class="debug-header">
          <div class="flex items-center gap-2">
            <i class="i-lucide-terminal text-amber-600"></i>
            <span class="font-semibold">Console de Debug - OpenAI</span>
            <span class="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">MODO DEBUG</span>
            <span v-if="debugModeReason" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded ml-2">
              {{ debugModeReason }}
            </span>
          </div>
        </div>
        
        <div class="debug-content">
          <div class="debug-section">
            <label class="debug-label">Agente Selecionado</label>
            <div class="text-sm text-slate-600">
              {{ selectedAgent?.name || 'Nenhum agente selecionado' }}
              <span v-if="selectedAgent" class="ml-2 text-xs">(ID: {{ selectedAgentId }})</span>
            </div>
          </div>
          
          <div class="debug-section">
            <label class="debug-label">Prompt de Instrução</label>
            <textarea
              v-model="debugCustomPrompt"
              class="debug-textarea"
              rows="4"
              placeholder="Digite o prompt de instrução personalizado..."
              @input="generateDebugFormattedOutput"
            />
          </div>
          
          <div class="debug-section">
            <div class="flex justify-between items-center mb-2">
              <label class="debug-label">Saída Formatada (JSON)</label>
              <div class="flex gap-2">
                <Button
                  @click="pasteAndPopulateFromClipboard"
                  size="sm"
                  variant="outline"
                  color="blue"
                  :disabled="!selectedAgent"
                >
                  <template #icon>
                    <i class="i-lucide-clipboard-paste"></i>
                  </template>
                  Colar JSON
                </Button>
                <Button
                  @click="copyDebugToClipboard"
                  size="sm"
                  variant="outline"
                  :disabled="!debugFormattedOutput"
                >
                  <template #icon>
                    <i class="i-lucide-clipboard"></i>
                  </template>
                  Copiar
                </Button>
              </div>
            </div>
            <div class="debug-output">
              <pre v-if="debugFormattedOutput">{{ debugFormattedOutput }}</pre>
              <div v-else class="text-center text-slate-500 py-4">
                Selecione um agente para ver a saída formatada
              </div>
            </div>
          </div>

          <!-- Botões de ação -->
          <div class="debug-section">
            <div class="flex gap-2 justify-end">
              <Button
                @click="applyDebugAdjustment"
                size="md"
                variant="solid"
                color="blue"
                :disabled="!debugCustomPrompt.trim() || !selectedAgent"
                :is-loading="isSubmitting"
              >
                <template #icon>
                  <i class="i-lucide-sparkles"></i>
                </template>
                Aplicar Ajustes
              </Button>
              <Button
                @click="showDebugResponseInput = !showDebugResponseInput"
                size="md"
                variant="outline"
                color="amber"
              >
                <template #icon>
                  <i :class="showDebugResponseInput ? 'i-lucide-x' : 'i-lucide-file-input'"></i>
                </template>
                {{ showDebugResponseInput ? 'Cancelar' : 'Colar Resposta' }}
              </Button>
            </div>
          </div>
          
          <!-- Manual Response Input Section -->
          <div v-if="showDebugResponseInput" class="debug-section">
            <div class="flex justify-between items-center mb-2">
              <label class="debug-label">Colar Resposta da OpenAI</label>
              <button
                @click="processDebugManualResponse"
                class="debug-process-btn"
                :disabled="!debugManualResponse.trim() || !selectedAgent"
              >
                <i class="i-lucide-play"></i>
                Processar Resposta
              </button>
            </div>
            <textarea
              v-model="debugManualResponse"
              class="debug-textarea"
              rows="8"
              placeholder="Cole aqui a resposta JSON da OpenAI com as alterações...&#10;&#10;Exemplo:&#10;{&#10;  &quot;alterations&quot;: [&#10;    {&#10;      &quot;variable&quot;: &quot;company_data.name&quot;,&#10;      &quot;action&quot;: &quot;alter&quot;,&#10;      &quot;newValue&quot;: &quot;Novo Nome&quot;&#10;    }&#10;  ]&#10;}"
            />
            <div class="text-xs text-amber-600 mt-2">
              <i class="i-lucide-info"></i>
              Cole o JSON de resposta da OpenAI. O sistema processará e filtrará apenas as alterações válidas.
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- conteudo -->
    <div ref="scrollContainer" class="flex-1 overflow-y-auto custom-scrollbar">
      <!-- Conteúdo principal (agora sempre visível, sem página inicial) -->
      <div class="w-full mx-auto max-w-6xl p-4 sm:p-6 lg:p-8">
        <!-- Progress indicator -->
        <div class="mb-8">
          <div class="flex items-center justify-between max-w-4xl mx-auto">
            <div class="flex items-center">
              <div class="flex items-center justify-center w-10 h-10 rounded-full"
                   :class="selectedAgentId ? 'bg-n-brand text-white' : 'bg-n-slate-3 text-n-slate-11'">
                <span class="text-sm font-semibold">1</span>
              </div>
              <span class="ml-3 text-sm font-medium"
                    :class="selectedAgentId ? 'text-n-slate-12' : 'text-n-slate-11'">
                Selecionar Agente
              </span>
            </div>
            <div class="flex-1 mx-4 h-0.5 bg-n-slate-3"></div>
            <div class="flex items-center">
              <div class="flex items-center justify-center w-10 h-10 rounded-full"
                   :class="step2Active ? 'bg-n-brand text-white' : 'bg-n-slate-3 text-n-slate-11'">
                <span class="text-sm font-semibold">2</span>
              </div>
              <span class="ml-3 text-sm font-medium"
                    :class="step2Active ? 'text-n-slate-12' : 'text-n-slate-11'">
                Configurar Ajuste
              </span>
            </div>
            <div class="flex-1 mx-4 h-0.5 bg-n-slate-3"></div>
            <div class="flex items-center">
              <div class="flex items-center justify-center w-10 h-10 rounded-full"
                   :class="step3Active ? 'bg-n-brand text-white' : 'bg-n-slate-3 text-n-slate-11'">
                <span class="text-sm font-semibold">3</span>
              </div>
              <span class="ml-3 text-sm font-medium"
                    :class="step3Active ? 'text-n-slate-12' : 'text-n-slate-11'">
                Revisar Alterações
              </span>
            </div>
          </div>
        </div>

        <!-- Seletor de Agente -->
        <div v-if="!showAlterations && !showModeSelection && !showAdjustmentArea" class="bg-white dark:bg-slate-900 rounded-md border border-n-weak p-6 mb-6">
          <div class="flex items-start gap-4 mb-6">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-n-brand/10 rounded-lg flex items-center justify-center">
                <i class="i-lucide-bot text-n-brand text-xl"></i>
              </div>
            </div>
            <div class="flex-1">
              <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
                Selecione o Agente
              </h2>
              <p class="text-n-slate-11">
                Escolha o agente que você deseja ajustar. Após a seleção, você poderá aplicar ajustes inteligentes ao comportamento do agente.
              </p>
            </div>
          </div>
          <AgentSelector
            :selected-agent-id="selectedAgentId"
            :disabled="!!selectedAgentId"
            @select="handleAgentSelect" />
        </div>

        <!-- Seleção de Modo: Ajuste Guiado vs Modo Diagnóstico -->
        <div v-if="showModeSelection && !showAlterations" class="animate-fade-in-up step-transition step-enter-active">
          <div class="bg-white dark:bg-slate-900 rounded-md border border-n-weak p-6 mb-6">
            <div class="flex items-start gap-4 mb-6">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-n-brand/10 rounded-lg flex items-center justify-center">
                  <i class="i-lucide-route text-n-brand text-xl"></i>
                </div>
              </div>
              <div class="flex-1">
                <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
                  Escolha o Tipo de Ajuste
                </h2>
                <p class="text-n-slate-11">
                  Selecione como você gostaria de otimizar o agente <strong>{{ selectedAgent?.name }}</strong>
                </p>
              </div>
            </div>

            <!-- Grid com as duas opções -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Opção A - Ajuste Guiado -->
              <div
                @click="handleModeSelect('guided')"
                class="mode-card group cursor-pointer bg-gradient-to-br from-n-slate-1 to-n-slate-2 border-2 border-n-slate-3 rounded-lg p-6 transition-all duration-200 hover:shadow-lg hover:border-n-slate-6 hover:-translate-y-1">
                <div class="flex items-start gap-4 mb-4">
                  <div class="w-12 h-12 bg-n-brand rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i class="i-lucide-target text-white text-2xl"></i>
                  </div>
                  <div class="flex-1">
                    <h3 class="text-xl font-bold text-n-slate-12 mb-1 group-hover:text-n-brand transition-colors">
                      Ajuste Guiado
                    </h3>
                    <span class="inline-block px-2 py-0.5 bg-n-slate-3 text-n-slate-11 text-xs font-medium rounded-full">
                      Padrão
                    </span>
                  </div>
                </div>

                <p class="text-lg font-semibold text-n-slate-12 mb-3">
                  Você sabe o que quer melhorar
                </p>

                <p class="text-sm text-n-slate-11 mb-4">
                  Escolha um objetivo específico ou deixe a IA propor melhorias direcionadas para o prompt do agente.
                </p>

                <div class="flex items-start gap-2 p-3 bg-white/50 dark:bg-n-slate-8/50 rounded-md">
                  <i class="i-lucide-info text-n-brand mt-0.5 flex-shrink-0"></i>
                  <p class="text-xs text-n-slate-11">
                    <strong>Ideal quando:</strong> Você já identificou um problema ou quer refinar um comportamento específico.
                  </p>
                </div>
              </div>

              <!-- Opção B - Modo Diagnóstico -->
              <div
                @click="handleModeSelect('diagnostic')"
                class="mode-card group cursor-pointer bg-gradient-to-br from-n-slate-1 to-n-slate-2 border-2 border-n-slate-3 rounded-lg p-6 transition-all duration-200 hover:shadow-lg hover:border-n-slate-6 hover:-translate-y-1 relative">
                <div class="flex items-start gap-4 mb-4">
                  <div class="w-12 h-12 bg-n-brand rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i class="i-lucide-scan-search text-white text-2xl"></i>
                  </div>
                  <div class="flex-1">
                    <h3 class="text-xl font-bold text-n-slate-12 mb-1 group-hover:text-n-brand transition-colors">
                      Modo Diagnóstico
                    </h3>
                    <span class="inline-block px-2 py-0.5 bg-n-slate-3 text-n-slate-11 text-xs font-medium rounded-full">
                      Automático
                    </span>
                  </div>
                </div>

                <p class="text-lg font-semibold text-n-slate-12 mb-3">
                  Quer que a IA encontre problemas e oportunidades
                </p>

                <p class="text-sm text-n-slate-11 mb-4">
                  O sistema analisa o prompt atual do agente em profundidade e identifica falhas estruturais, riscos e melhorias possíveis.
                </p>

                <div class="flex items-start gap-2 p-3 bg-white/50 dark:bg-n-slate-8/50 rounded-md">
                  <i class="i-lucide-lightbulb text-n-brand mt-0.5 flex-shrink-0"></i>
                  <p class="text-xs text-n-slate-11">
                    <strong>Recomendado quando:</strong> O agente cresceu ao longo do tempo ou apresenta comportamentos imprevisíveis.
                  </p>
                </div>
              </div>
            </div>

            <!-- Botão Voltar -->
            <div class="flex justify-start mt-6">
              <Button
                label="Voltar"
                variant="outline"
                color="slate"
                size="md"
                @click="() => {
                  showModeSelection = false;
                  selectedAgentId = null;
                  selectedAgent = null;
                  selectedMode = null;
                }">
                <template #icon>
                  <i class="i-lucide-arrow-left"></i>
                </template>
              </Button>
            </div>
          </div>
        </div>

        <!-- Área de Diagnóstico -->
        <div v-if="showDiagnostic && !showAlterations" class="animate-fade-in-up step-transition step-enter-active">
          <div class="bg-white dark:bg-slate-900 rounded-md border border-n-weak p-6 mb-6">
            <div class="flex items-start gap-4 mb-6">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-n-brand/10 rounded-lg flex items-center justify-center">
                  <i class="i-lucide-scan-search text-n-brand text-xl"></i>
                </div>
              </div>
              <div class="flex-1">
                <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
                  Diagnóstico do Agente
                </h2>
                <p class="text-n-slate-11">
                  Análise completa do agente <strong>{{ selectedAgent?.name }}</strong>
                </p>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="isDiagnosticLoading" class="py-12">
              <div class="flex flex-col items-center justify-center gap-4">
                <div class="w-16 h-16 bg-n-brand/10 rounded-full flex items-center justify-center animate-pulse">
                  <i class="i-lucide-brain text-n-brand text-2xl"></i>
                </div>
                <div class="text-center">
                  <h3 class="text-sm font-semibold text-n-slate-12 mb-2">
                    Analisando Agente Inteligente
                  </h3>
                  <p class="text-xs text-n-slate-11 min-h-[20px]">
                    {{ diagnosticLoadingMessages[diagnosticMessageIndex] }}
                  </p>
                </div>
                <div class="flex items-center gap-3 mt-2">
                  <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                  <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                  <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                </div>
              </div>
            </div>

            <!-- Diagnostic Result -->
            <div v-else-if="diagnosticResult" class="diagnostic-result">
              <div class="prose prose-slate max-w-none dark:prose-invert">
                <div class="rounded-lg p-6 markdown-content" v-html="renderMarkdown(diagnosticResult)"></div>
              </div>
              <div v-if="isDebugMode && diagnosticOriginalResult" class="mt-6">
                <div class="flex items-center gap-2 text-xs text-n-slate-11 mb-2">
                  <i class="i-lucide-bug text-n-brand"></i>
                  <span>Diagnóstico original (sem simplificação) - visível apenas em modo debug</span>
                </div>
                <div class="rounded-lg p-4 border border-dashed border-n-weak bg-n-slate-1 markdown-content" v-html="renderMarkdown(diagnosticOriginalResult)"></div>
              </div>
              <div v-if="isDebugMode && diagnosticMappedResult" class="mt-6">
                <div class="flex items-center gap-2 text-xs text-n-slate-11 mb-2">
                  <i class="i-lucide-code text-n-amber-9"></i>
                  <span>Diagnóstico com mapeamento de campos (sem GPT-5) - visível apenas em modo debug</span>
                </div>
                <div class="rounded-lg p-4 border border-dashed border-n-amber-6 bg-n-amber-1 markdown-content" v-html="renderMarkdown(diagnosticMappedResult)"></div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div v-if="!isDiagnosticLoading && diagnosticResult" class="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 p-6 bg-n-slate-1 rounded-md border border-n-weak">
            <div class="flex items-center gap-3 text-sm">
              <i class="i-lucide-info-circle text-n-slate-11"></i>
              <span class="text-n-slate-11">
                Com base neste diagnóstico, você pode aplicar ajustes automáticos
              </span>
            </div>
            <div class="flex gap-3">
              <Button
                label="Voltar"
                variant="outline"
                color="slate"
                size="lg"
                @click="() => {
                  showDiagnostic = false;
                  showModeSelection = true;
                  diagnosticResult = '';
                  diagnosticOriginalResult = '';
                }">
                <template #icon>
                  <i class="i-lucide-arrow-left"></i>
                </template>
              </Button>
              <Button
                label="Aplicar Ajustes Sugeridos"
                color="blue"
                size="lg"
                :is-loading="isSubmitting"
                @click="applyDiagnosticAdjustments">
                <template #icon>
                  <i class="i-lucide-sparkles"></i>
                </template>
              </Button>
            </div>
          </div>
        </div>

        <!-- Área de Alterações após processamento -->
        <div v-if="showAlterations" class="animate-fade-in-up step-transition step-enter-active">
          <div class="bg-white dark:bg-slate-900 rounded-md border border-n-weak p-6">
            <div class="flex items-start gap-4 mb-6">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-n-teal-2 rounded-lg flex items-center justify-center">
                  <i class="i-lucide-check-circle text-n-teal-11 text-xl"></i>
                </div>
              </div>
              <div class="flex-1">
                <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
                  Ajustes Processados com Sucesso
                </h2>
                <p class="text-n-slate-11">
                  Revise as alterações identificadas abaixo. Por enquanto, esta é apenas uma visualização - nenhuma alteração foi aplicada ao agente.
                </p>
              </div>
            </div>
          
            <!-- Alterations Display -->
            <div v-if="processedAlterations.length > 0" class="mb-6">
              <div class="mb-4">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="text-md font-semibold text-n-slate-12">
                    Alterações Identificadas
                  </h3>
                  <!-- Debug button (only visible in debug mode) -->
                  <div v-if="isDebugMode && alterationsOriginalJson" class="flex items-center gap-2">
                    <Button
                      @click="showAlterationsDebug = !showAlterationsDebug"
                      size="sm"
                      variant="outline"
                      color="amber"
                      :label="showAlterationsDebug ? 'Ocultar JSON Debug' : 'Ver JSON Debug'"
                    >
                      <template #icon>
                        <i :class="showAlterationsDebug ? 'i-lucide-eye-off' : 'i-lucide-code-2'"></i>
                      </template>
                    </Button>
                  </div>
                </div>
                <p class="text-sm text-n-slate-11 mb-4">
                  Selecione as alterações que deseja aplicar ao agente:
                </p>

                <!-- Debug JSON Display (only visible when toggled) -->
                <div v-if="isDebugMode && showAlterationsDebug && alterationsOriginalJson" class="mb-4">
                  <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center gap-2">
                        <i class="i-lucide-code-2 text-amber-600"></i>
                        <span class="text-sm font-semibold text-amber-800">JSON Original (Debug)</span>
                      </div>
                      <Button
                        @click="copyAlterationsJsonToClipboard"
                        size="sm"
                        variant="ghost"
                        color="amber"
                      >
                        <template #icon>
                          <i class="i-lucide-clipboard"></i>
                        </template>
                        Copiar
                      </Button>
                    </div>
                    <div class="bg-slate-900 text-slate-100 rounded p-4 overflow-x-auto max-h-96 overflow-y-auto">
                      <pre class="text-xs font-mono">{{ JSON.stringify(alterationsOriginalJson, null, 2) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="space-y-3">
                <AlterationDisplay
                  v-for="(alteration, index) in processedAlterations"
                  :key="`${alteration.variable}-${alteration.itemName || index}`"
                  :alteration="alteration"
                  :current-value="getCurrentValue(alteration)"
                  :selected="isAlterationSelected(alteration)"
                  :system-labels="systemLabels"
                  :system-teams="systemTeams"
                  @toggle="toggleAlteration"
                />
              </div>
              
              <div class="mt-4 p-3 bg-n-slate-1 rounded-lg">
                <div class="flex items-center justify-between">
                  <p class="text-sm text-n-slate-12">
                    <strong>{{ selectedAlterations.length }}</strong> de <strong>{{ processedAlterations.length }}</strong> alterações selecionadas
                  </p>
                  <!-- Botão de reportar problema -->
                  <Button
                    @click="reportAdjustmentIssue"
                    size="sm"
                    variant="ghost"
                    color="slate"
                    title="Reportar problema no ajuste"
                  >
                    <template #icon>
                      <i class="i-lucide-alert-circle"></i>
                    </template>
                    <span class="text-xs">Reportar problema</span>
                  </Button>
                </div>
              </div>
            </div>
            
            <!-- No alterations message -->
            <div v-else class="mb-6">
              <div class="bg-n-amber-1 border border-n-amber-3 rounded-lg p-4">
                <p class="text-n-amber-11 text-sm">
                  Nenhuma alteração foi identificada para este ajuste.
                </p>
              </div>
            </div>
          
            <div class="flex justify-end gap-3">
              <Button 
                label="Novo Ajuste" 
                variant="outline"
                color="slate"
                size="lg"
                @click="resetForm">
                <template #icon>
                  <i class="i-lucide-refresh-cw"></i>
                </template>
              </Button>
              <Button 
                label="Aplicar Alterações Selecionadas" 
                color="blue"
                size="lg"
                :disabled="selectedAlterations.length === 0"
                :is-loading="isSubmitting"
                @click="handleSaveAlterations">
                <template #icon>
                  <i class="i-lucide-save"></i>
                </template>
              </Button>
            </div>
          </div>
        </div>

        <!-- Área de seleção de ajuste -->
        <div v-if="showAdjustmentArea && !showAlterations" class="animate-fade-in-up step-transition step-enter-active">
          <div class="bg-white dark:bg-slate-900 rounded-md border border-n-weak p-6">
            <div class="flex items-start gap-4 mb-6">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-n-brand/10 rounded-lg flex items-center justify-center">
                  <i class="i-lucide-sparkles text-n-brand text-xl"></i>
                </div>
              </div>
              <div class="flex-1">
                <h2 class="text-lg font-semibold text-n-slate-12 mb-1">
                  Configure o Ajuste
                </h2>
                <p class="text-n-slate-11">
                  Digite uma instrução personalizada ou gere sugestões automáticas usando IA.
                </p>
              </div>
            </div>

            <!-- Campo de prompt personalizado primeiro -->
            <CustomPromptInput
              :value="customPrompt"
              :disabled="!!selectedOption || isLoading"
              @update="handleCustomPromptChange" />

            <div class="my-6 flex items-center">
              <div class="flex-1 border-t border-n-slate-3"></div>
              <div class="px-4 flex items-center gap-2">
                <span class="text-sm font-medium text-n-slate-11">ou</span>
              </div>
              <div class="flex-1 border-t border-n-slate-3"></div>
            </div>

            <!-- Botão para gerar sugestões ou estado de loading -->
            <div v-if="adjustmentOptions.length === 0" class="flex flex-col items-center justify-center py-6 bg-n-slate-1 rounded-lg border border-n-slate-3 border-dashed">
              <!-- Estado de Loading -->
              <div v-if="isLoading" class="flex flex-col items-center justify-center gap-4">
                <div class="w-16 h-16 bg-n-brand/10 rounded-full flex items-center justify-center animate-pulse">
                  <i class="i-lucide-sparkles text-n-brand text-2xl"></i>
                </div>
                <div class="text-center">
                  <h3 class="text-sm font-semibold text-n-slate-12 mb-2">
                    Gerando Ajustes Inteligentes
                  </h3>
                  <p class="text-xs text-n-slate-11">
                    {{ loadingMessages[loadingMessageIndex] }}
                  </p>
                </div>
                <div class="flex items-center gap-3 mt-2">
                  <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                  <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                  <div class="w-2 h-2 bg-n-brand rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                </div>
              </div>

              <!-- Estado Inicial -->
              <template v-else>
                <div class="text-center mb-4">
                  <i class="i-lucide-lightbulb text-n-brand text-3xl mb-2"></i>
                  <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
                    Precisa de ideias?
                  </h3>
                  <p class="text-xs text-n-slate-11 max-w-md">
                    Nossa IA pode analisar o agente e sugerir ajustes inteligentes personalizados
                  </p>
                </div>
                <Button
                  label="Gerar Sugestões com IA"
                  color="blue"
                  variant="outline"
                  size="md"
                  :disabled="!!customPrompt"
                  @click="fetchAdjustmentOptions">
                  <template #icon>
                    <i class="i-lucide-sparkles"></i>
                  </template>
                </Button>
              </template>
            </div>

            <!-- Blocos de sugestões (só aparece depois de gerar) -->
            <div v-else>
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Sugestões Geradas
                </h3>
                <Button
                  label="Gerar Novas Sugestões"
                  color="blue"
                  variant="ghost"
                  size="sm"
                  @click="fetchAdjustmentOptions">
                  <template #icon>
                    <i class="i-lucide-refresh-cw"></i>
                  </template>
                </Button>
              </div>
              <AdjustmentBlocks
                :options="adjustmentOptions"
                :selected-option="selectedOption"
                :is-loading="isLoading"
                @select="handleBlockSelect" />
            </div>
          </div>

          <div class="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 p-6 bg-n-slate-1 rounded-md border border-n-weak">
            <div class="flex items-center gap-3 text-sm">
              <i class="i-lucide-info-circle text-n-slate-11"></i>
              <span class="text-n-slate-11">
                O ajuste será aplicado permanentemente ao agente selecionado
              </span>
            </div>
            <div class="flex gap-3">
              <Button 
                label="Cancelar" 
                variant="outline" 
                color="slate"
                size="lg"
                @click="() => {
                  selectedAgentId = null;
                  selectedAgent = null;
                  showAdjustmentArea = false;
                  adjustmentOptions = [];
                  selectedOption = null;
                  customPrompt = '';
                }" />
              <Button 
                label="Aplicar Ajuste" 
                color="blue"
                size="lg"
                :is-loading="isSubmitting"
                :disabled="!isFormValid"
                @click="handleSubmit">
                <template #icon>
                  <i class="i-lucide-sparkles"></i>
                </template>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Existing shimmer animation */
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in-scale {
  animation: fadeInScale 0.3s ease-in-out;
}

/* Fade animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out forwards;
}

.animate-fade-out {
  animation: fadeOut 0.3s ease-in forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Transições suaves entre blocos */
.step-transition {
  transition: all 0.4s ease-in-out;
}

.step-enter {
  opacity: 0;
  transform: translateY(30px) scale(0.98);
}

.step-enter-active {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Bounce animation with delays */
@keyframes bounce {
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
}

.animate-bounce {
  animation: bounce 1s ease-in-out infinite;
}

/* Barra de rolagem personalizada para o container principal */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: background-color 0.3s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Tema escuro */
.dark .custom-scrollbar {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(255, 255, 255, 0.4);
}

/* Debug Console Styles */
.debug-console-container {
  position: relative;
  background: #fffbeb;
  border-bottom: 2px solid #f59e0b;
  max-height: 400px;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.debug-console {
  padding: 1rem;
}

.debug-header {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #fcd34d;
}

.debug-content {
  display: grid;
  gap: 1rem;
}

.debug-section {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #fde68a;
}

.debug-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #92400e;
  margin-bottom: 0.5rem;
  letter-spacing: 0.05em;
}

.debug-prompts-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.debug-prompt-btn {
  padding: 0.25rem 0.75rem;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.debug-prompt-btn:hover {
  background: #fde68a;
  border-color: #f59e0b;
}

.debug-prompt-btn.active {
  background: #f59e0b;
  color: white;
  border-color: #d97706;
}

.debug-textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #fcd34d;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: monospace;
  resize: vertical;
}

.debug-output {
  background: #1e293b;
  border-radius: 0.25rem;
  max-height: 300px;
  overflow: auto;
  color: #e2e8f0;
}

.debug-output pre {
  padding: 1rem;
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.75rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Dark mode debug console */
.dark .debug-console-container {
  background: #451a03;
  border-bottom-color: #92400e;
}

.dark .debug-section {
  background: #1e293b;
  border-color: #92400e;
}

.dark .debug-label {
  color: #fbbf24;
}

.dark .debug-prompt-btn {
  background: #451a03;
  border-color: #92400e;
  color: #fde68a;
}

.dark .debug-prompt-btn:hover {
  background: #78350f;
}

.dark .debug-prompt-btn.active {
  background: #92400e;
  color: white;
}

.dark .debug-textarea {
  background: #0f172a;
  border-color: #92400e;
  color: #e2e8f0;
}

/* Process button styles */
.debug-process-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border: 1px solid #059669;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.debug-process-btn:hover:not(:disabled) {
  background: #059669;
  border-color: #047857;
}

.debug-process-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #6b7280;
  border-color: #4b5563;
}

.dark .debug-process-btn {
  background: #10b981;
  border-color: #059669;
}

.dark .debug-process-btn:hover:not(:disabled) {
  background: #059669;
  border-color: #047857;
}

.dark .debug-process-btn:disabled {
  background: #374151;
  border-color: #4b5563;
}

/* Mode selection cards */
.mode-card {
  position: relative;
  overflow: hidden;
}

.mode-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s;
}

.mode-card:hover::before {
  left: 100%;
}

/* Markdown content styles */
.markdown-content {
  line-height: 1.6;
  color: #1e293b; /* Cor escura para texto no tema claro */
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3 {
  color: #0f172a; /* Títulos bem escuros no tema claro */
  font-weight: 600;
}

.markdown-content ul,
.markdown-content ol {
  padding-left: 1.5rem;
}

.markdown-content li {
  margin-bottom: 0.5rem;
  color: #334155; /* Cinza escuro para listas */
}

.markdown-content pre {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  background: #f8fafc !important; /* Fundo cinza bem claro */
  color: #1e293b !important;
}

.markdown-content code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: #f1f5f9 !important;
  color: #0f172a !important;
  padding: 0.125rem 0.25rem;
}

.markdown-content blockquote {
  color: #475569;
  border-left-color: #cbd5e1;
}

.markdown-content p {
  color: #334155; /* Parágrafos em cinza escuro */
}

.markdown-content strong {
  color: #0f172a; /* Negrito bem escuro */
  font-weight: 600;
}

/* Dark mode - mantém como estava */
.dark .markdown-content {
  color: #e2e8f0;
}

.dark .markdown-content h1,
.dark .markdown-content h2,
.dark .markdown-content h3 {
  color: #f1f5f9;
}

.dark .markdown-content li,
.dark .markdown-content p {
  color: #cbd5e1;
}

.dark .markdown-content blockquote {
  color: #94a3b8;
  border-left-color: #475569;
}

.dark .markdown-content pre {
  background: #1e293b !important;
  color: #e2e8f0 !important;
}

.dark .markdown-content code {
  background: #334155 !important;
  color: #f1f5f9 !important;
}

.dark .markdown-content strong {
  color: #f8fafc;
}

</style>
